import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ethers } from 'ethers'

import { shortAddress } from './marketplaceModel'

export function CopyButton({ value, copyValue, label = 'Copy', compact = true }) {
    if (!value) return null
    return <button type="button" className={`market-copy ${compact ? 'is-compact' : ''}`} onClick={(event) => { event.stopPropagation(); copyValue(value) }} title={label} aria-label={label}>
        <i className="far fa-copy" />
    </button>
}

export function AddressValue({ value, copyValue, label, mono = true }) {
    return <span className={`market-address ${mono ? 'is-mono' : ''}`}>
        <span title={value}>{shortAddress(value)}</span>
        <CopyButton value={value} copyValue={copyValue} label={label} />
    </span>
}

export function ProjectLogo({ logo, symbol = 'NFT', loading = false, loadingLabel, className = '' }) {
    const [failedLogo, setFailedLogo] = useState('')
    const hasLogo = Boolean(logo) && failedLogo !== logo
    const classes = `market-project-logo ${className} ${loading ? 'is-loading' : hasLogo ? 'has-image' : 'is-fallback'}`

    if (loading) {
        return <span className={classes} role="status" aria-label={loadingLabel}>
            <i className="fa fa-circle-notch fa-spin" />
        </span>
    }
    if (hasLogo) {
        return <span className={classes}>
            <img src={logo} alt={`${symbol} logo`} onError={() => setFailedLogo(logo)} />
        </span>
    }
    return <span className={classes} aria-label={`${symbol} logo`}>
        <strong>{String(symbol || 'NFT').slice(0, 3)}</strong>
    </span>
}

function useMobileViewport() {
    const [mobile, setMobile] = useState(() => typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 760px)').matches)

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
        const query = window.matchMedia('(max-width: 760px)')
        const update = () => setMobile(query.matches)
        update()
        query.addEventListener?.('change', update)
        return () => query.removeEventListener?.('change', update)
    }, [])

    return mobile
}

export function canAccessMarketAdmin(market) {
    const account = market?.wallet?.account?.toLowerCase()
    if (!account) return false
    return [market?.core?.owner, market?.core?.pendingOwner]
        .filter(Boolean)
        .some((address) => address.toLowerCase() === account)
}

export function MarketplaceHeader({ market, t, language, onLanguage, onConnect, onDisconnect, onSwitchNetwork, onAdmin }) {
    const { network, wallet } = market
    const projectLogo = market.project?.logo || market.project?.image || ''
    const nftSymbol = market.core?.nftSymbol || 'NFT'
    const [open, setOpen] = useState(false)
    const [connecting, setConnecting] = useState('')
    const mobile = useMobileViewport()
    const walletRef = useRef(null)
    const menuRef = useRef(null)
    const networkMatches = wallet.account && Number(wallet.chainId) === Number(market.chainId)
    const canAccessAdmin = canAccessMarketAdmin(market)
    const walletState = wallet.account ? (networkMatches ? 'is-online' : 'is-warning') : ''

    useEffect(() => {
        if (!open) return undefined
        const close = (event) => {
            if (!walletRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false)
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [open])

    useEffect(() => {
        if (!open || !mobile) return undefined
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [mobile, open])

    const connect = async (id) => {
        setConnecting(id)
        try {
            await onConnect(id)
            setOpen(false)
        } finally {
            setConnecting('')
        }
    }

    const walletMenu = <div className="market-wallet__menu" ref={menuRef}>
        <div className="market-wallet__title">{t('selectWallet')}</div>
        {wallet.account && !networkMatches && <button type="button" className="market-wallet__switch" onClick={onSwitchNetwork}>
            <i className="fa fa-exchange-alt" /> {t('switchNetwork')}
        </button>}
        {wallet.wallets.map((item) => <button type="button" key={item.id} onClick={() => connect(item.id)} disabled={Boolean(connecting)}>
            {item.icon ? <img src={item.icon} alt="" /> : <i className="fa fa-wallet" />}
            <span>{item.name}</span>
            {connecting === item.id && <i className="fa fa-spinner fa-spin" />}
            {wallet.walletId === item.id && wallet.account && <i className="fa fa-check-circle" />}
        </button>)}
        {!wallet.wallets.length && <p><i className="fa fa-exclamation-circle" /> {t('noWallet')}</p>}
        {wallet.account && <button type="button" className="market-wallet__disconnect" onClick={() => { onDisconnect(); setOpen(false) }}>
            <i className="fa fa-sign-out-alt" /> {t('disconnect')}
        </button>}
    </div>

    return <header className="market-header">
        <div className="market-header__brand">
            <ProjectLogo className="market-header__logo" logo={projectLogo} symbol={nftSymbol} loading={market.projectLoading} loadingLabel={t('loadingProjectLogo')} />
            <span><strong>{nftSymbol}</strong><small>{t('marketplace')}</small></span>
        </div>
        <div className="market-header__actions">
            <div className={`market-network ${networkMatches ? 'is-online' : wallet.account ? 'is-warning' : ''}`}>
                {network?.logo ? <img src={['bsc', 'bsc-test'].includes(network.shortname) ? '/img/binance.png' : network.logo} alt="" onError={(event) => { event.currentTarget.style.display = 'none' }} /> : <i className="fa fa-network-wired" />}
                <span>{network?.name || 'Unknown network'}</span>
                <i className="market-network__dot" />
                <small>{networkMatches ? t('connected') : wallet.account ? t('wrongNetwork') : t('connect')}</small>
            </div>
            <div className="market-language" aria-label="Language">
                <button type="button" className={language.startsWith('en') ? 'is-active' : ''} onClick={() => onLanguage('en')}>EN</button>
                <span>/</span>
                <button type="button" className={language.startsWith('zh') ? 'is-active' : ''} onClick={() => onLanguage('zh')}>中文</button>
            </div>
            <div className="market-wallet" ref={walletRef}>
                <button
                    type="button"
                    className={`market-wallet__trigger ${walletState}`}
                    aria-label={wallet.account ? `${networkMatches ? t('connected') : t('wrongNetwork')} ${shortAddress(wallet.account)}` : t('connect')}
                    onClick={() => setOpen((value) => !value)}
                >
                    <i className="fa fa-wallet" />
                    <span className="market-wallet__desktop-label">{wallet.account ? shortAddress(wallet.account) : t('connect')}</span>
                    <span className="market-wallet__mobile-label">
                        {wallet.account && <i className={`market-wallet__state-dot ${walletState}`} title={networkMatches ? t('connected') : t('wrongNetwork')} />}
                        {wallet.account ? shortAddress(wallet.account, 4, 4) : t('connect')}
                    </span>
                    <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} />
                </button>
                {open && (!mobile || typeof document === 'undefined') && walletMenu}
            </div>
            {open && mobile && typeof document !== 'undefined' && createPortal(
                <div className="market-wallet-sheet-layer" onPointerDown={(event) => {
                    if (event.target === event.currentTarget) setOpen(false)
                }}>
                    {walletMenu}
                </div>,
                document.body
            )}
            {canAccessAdmin && <button type="button" className="market-header__admin" onClick={onAdmin}><i className="far fa-user-circle" /> <span>{t('admin')}</span></button>}
        </div>
    </header>
}

export function MarketSelect({ label, value, options, onChange, icon = '', className = '' }) {
    const [open, setOpen] = useState(false)
    const mobile = useMobileViewport()
    const rootRef = useRef(null)
    const menuRef = useRef(null)
    const selected = options.find((option) => option.value === value) || options[0]

    useEffect(() => {
        if (!open) return undefined
        const close = (event) => {
            if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false)
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [open])

    useEffect(() => {
        if (!open || !mobile) return undefined
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [mobile, open])

    const choose = (nextValue) => {
        onChange(nextValue)
        setOpen(false)
    }
    const menu = <div className="market-select__menu" ref={menuRef} role="listbox" aria-label={label}>
        {mobile && <div className="market-select__sheet-title">
            <strong>{label}</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"><i className="fa fa-times" /></button>
        </div>}
        {options.map((option) => <button
            type="button"
            role="option"
            aria-selected={option.value === value}
            className={option.value === value ? 'is-selected' : ''}
            key={option.value}
            onClick={() => choose(option.value)}
        >
            <span>{option.label}</span>
            {option.value === value && <i className="fa fa-check" />}
        </button>)}
    </div>

    return <div className={`market-select ${className} ${open ? 'is-open' : ''}`} ref={rootRef}>
        {icon && <i className={`fa ${icon}`} />}
        <button
            type="button"
            className="market-select__trigger"
            aria-label={`${label}：${selected?.label || ''}`}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
        >
            <span>{selected?.label}</span>
            <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} />
        </button>
        {open && (!mobile || typeof document === 'undefined') && menu}
        {open && mobile && typeof document !== 'undefined' && createPortal(
            <div className="market-select-sheet-layer" onPointerDown={(event) => {
                if (event.target === event.currentTarget) setOpen(false)
            }}>
                {menu}
            </div>,
            document.body
        )}
    </div>
}

export function NftMedia({ entry, alt }) {
    const imageRef = useRef(null)
    const [loadedImage, setLoadedImage] = useState({ source: '', aspectRatio: '' })
    const image = entry?.metadata?.image || '/img/marketplace-fallback.jpg'

    useEffect(() => {
        const element = imageRef.current
        if (!entry || !element?.complete || element.naturalWidth <= 0 || element.naturalHeight <= 0) return
        setLoadedImage({
            source: image,
            aspectRatio: `${element.naturalWidth} / ${element.naturalHeight}`,
        })
    }, [entry, image])

    const imageReady = loadedImage.source === image
    const aspectRatio = imageReady ? loadedImage.aspectRatio : ''
    const loading = !entry || !imageReady
    return <div className="market-nft-media" style={aspectRatio ? { '--market-nft-aspect-ratio': aspectRatio } : undefined}>
        {entry && <img
            ref={imageRef}
            className={loading ? 'is-loading' : ''}
            loading="lazy"
            src={image}
            alt={alt}
            onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget
                setLoadedImage({
                    source: image,
                    aspectRatio: naturalWidth > 0 && naturalHeight > 0 ? `${naturalWidth} / ${naturalHeight}` : '',
                })
            }}
            onError={(event) => {
                if (!event.currentTarget.src.endsWith('/img/marketplace-fallback.jpg')) {
                    event.currentTarget.src = '/img/marketplace-fallback.jpg'
                    return
                }
                setLoadedImage({ source: image, aspectRatio: '' })
            }}
        />}
        {loading && <span className="market-nft-media__loading" role="status" aria-label="NFT image loading"><i className="fa fa-circle-notch fa-spin" /></span>}
        {entry?.error && <span className="market-nft-media__error" title={entry.error}><i className="fa fa-exclamation-triangle" /></span>}
    </div>
}

export function NftCard({
    tokenId,
    metadata,
    description,
    price,
    symbol,
    status,
    selected,
    onSelect,
    onOpen,
    actionLabel,
    onAction,
    seller,
    expiresAt,
    language,
    sellerLabel,
    remainingLabel,
    listingCard = false,
    selectionMode = false,
}) {
    const id = String(tokenId)
    const name = metadata?.metadata?.name || `NFT #${id}`
    const summary = description ?? metadata?.metadata?.description ?? ''
    const select = () => onSelect?.(id)
    const open = (event) => {
        event?.stopPropagation()
        onOpen?.()
    }
    const selectFromKeyboard = (event) => {
        if (!selectionMode || !['Enter', ' '].includes(event.key)) return
        event.preventDefault()
        select()
    }

    return <article
        className={`market-nft-card ${listingCard ? 'is-listing' : 'is-owned'} ${selectionMode ? 'is-selectable' : onOpen ? 'is-interactive' : ''} ${selected ? 'is-selected' : ''}`}
        role={selectionMode ? 'checkbox' : undefined}
        aria-label={selectionMode ? name : undefined}
        aria-checked={selectionMode ? Boolean(selected) : undefined}
        tabIndex={selectionMode ? 0 : undefined}
        onClick={selectionMode ? select : onOpen ? open : undefined}
        onKeyDown={selectFromKeyboard}
    >
        <button type="button" className="market-nft-card__media-button" onClick={selectionMode ? undefined : open} aria-label={selectionMode ? undefined : name} tabIndex={selectionMode ? -1 : undefined}>
            <NftMedia entry={metadata} alt={name} />
        </button>
        {onSelect && <label className="market-check market-nft-card__check" aria-hidden={selectionMode || undefined} onClick={(event) => event.stopPropagation()}>
            <input type="checkbox" checked={Boolean(selected)} onChange={select} tabIndex={selectionMode ? -1 : undefined} />
            <span><i className="fa fa-check" /></span>
        </label>}
        {status && <span className="market-nft-card__status">{status}</span>}
        <div className="market-nft-card__body">
            <small className="market-nft-card__token">#{id}</small>
            <button type="button" className="market-nft-card__title" onClick={selectionMode ? undefined : open} tabIndex={selectionMode ? -1 : undefined} title={name}>{name}</button>
            {summary && <p className="market-nft-card__description" title={summary}>{summary}</p>}
            {price != null && <p className="market-nft-card__price"><span>{price}</span> <small>{symbol}</small></p>}
            {listingCard && <div className="market-nft-card__meta">
                <span>{sellerLabel} <i className="far fa-user" /> {shortAddress(seller)}</span>
                <span>{remainingLabel} <i className="far fa-clock" /> {formatRemaining(expiresAt, language)}</span>
            </div>}
            {actionLabel && <button type="button" className="market-nft-card__action" onClick={(event) => { event.stopPropagation(); onAction?.() }}>{actionLabel}</button>}
        </div>
    </article>
}

export function NftDescription({ description, t }) {
    const [expanded, setExpanded] = useState(false)
    if (!description) return null

    return <section className="market-nft-description">
        <header><span>{t('description')}</span></header>
        <p className={`market-nft-description__text ${expanded ? '' : 'is-collapsed'}`}>{description}</p>
        <button type="button" onClick={() => setExpanded((value) => !value)} aria-label={t(expanded ? 'collapseDescription' : 'expandDescription')}>
            {t(expanded ? 'collapseDescription' : 'expandDescription')}
            <i className={`fa fa-chevron-${expanded ? 'up' : 'down'}`} />
        </button>
    </section>
}

export function Pagination({ page, total, pageSize, onPage, t }) {
    const pages = Math.max(1, Math.ceil(total / pageSize))
    if (total <= pageSize) return null
    return <nav className="market-pagination" aria-label="Pagination">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} title={t('previous')}><i className="fa fa-chevron-left" /></button>
        <span>{t('page', { page, pages })}</span>
        <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pages} title={t('next')}><i className="fa fa-chevron-right" /></button>
    </nav>
}

export function EmptyState({ icon = 'fa-store', title, text, action }) {
    return <div className="market-empty">
        <i className={`fa ${icon}`} />
        <strong>{title}</strong>
        {text && <p>{text}</p>}
        {action}
    </div>
}

export function MarketDialog({ title, eyebrow, icon = 'fa-bolt', children, footer, onClose, wide = false }) {
    useEffect(() => {
        const listener = (event) => { if (event.key === 'Escape') onClose() }
        document.addEventListener('keydown', listener)
        document.body.classList.add('market-dialog-open')
        return () => {
            document.removeEventListener('keydown', listener)
            document.body.classList.remove('market-dialog-open')
        }
    }, [onClose])

    return createPortal(<div className="market-dialog-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
        <section className={`market-dialog ${wide ? 'is-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
            <header>
                <span className="market-dialog__icon"><i className={`fa ${icon}`} /></span>
                <div>{eyebrow && <small>{eyebrow}</small>}<h2>{title}</h2></div>
                <button type="button" onClick={onClose} title="Close"><i className="fa fa-times" /></button>
            </header>
            <div className="market-dialog__body">{children}</div>
            {footer && <footer>{footer}</footer>}
        </section>
    </div>, document.body)
}

export function Toast({ toast, t, onClose, copyValue }) {
    useEffect(() => {
        if (!toast) return undefined
        const timer = window.setTimeout(onClose, 3200)
        return () => window.clearTimeout(timer)
    }, [onClose, toast])
    if (!toast) return null
    const message = t(toast.message, toast.values)
    return createPortal(<div className={`market-toast is-${toast.type || 'success'}`} role="status">
        <i className={`fa ${toast.type === 'error' ? 'fa-times-circle' : toast.type === 'info' ? 'fa-circle-notch fa-spin' : 'fa-check-circle'}`} />
        <span><strong>{message}</strong>{toast.txHash && <small>{shortAddress(toast.txHash)} <CopyButton value={toast.txHash} copyValue={copyValue} /></small>}</span>
        <button type="button" onClick={onClose}><i className="fa fa-times" /></button>
    </div>, document.body)
}

export function TransactionSuccessDialog({ result, t, explorer, copyValue, onClose }) {
    if (!result) return null
    const transactionUrl = explorer && result.txHash ? `${explorer}/tx/${result.txHash}` : ''
    const title = t(result.message || 'operationSuccess')

    return <MarketDialog
        title={title}
        eyebrow={t('transactionConfirmed')}
        icon="fa-check"
        onClose={onClose}
        footer={<>
            {transactionUrl && <a className="market-button" href={transactionUrl} target="_blank" rel="noreferrer">
                <i className="fa fa-external-link-alt" /> {t('viewTransaction')}
            </a>}
            <button type="button" className="market-button is-primary" onClick={onClose}>{t('done')}</button>
        </>}
    >
        <div className="market-transaction-success">
            <span className="market-transaction-success__icon"><i className="fa fa-check" /></span>
            <h3>{title}</h3>
            <p>{t('transactionSuccessHint')}</p>
            {result.txHash && <div className="market-transaction-success__hash">
                <span>{t('transactionHash')}</span>
                <strong title={result.txHash}>{shortAddress(result.txHash, 10, 8)}</strong>
                <CopyButton value={result.txHash} copyValue={copyValue} label={t('copyTransactionHash')} />
            </div>}
        </div>
    </MarketDialog>
}

export function formatAmount(value, decimals, maximumFractionDigits = 5) {
    if (value == null) return '0'
    const exact = ethers.utils.formatUnits(value, decimals)
    const [whole, fraction = ''] = exact.split('.')
    const compact = fraction.slice(0, maximumFractionDigits).replace(/0+$/, '')
    return compact ? `${whole}.${compact}` : whole
}

export function formatDate(timestamp, language) {
    if (!timestamp) return '—'
    return new Intl.DateTimeFormat(language.startsWith('zh') ? 'zh-CN' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(timestamp * 1000))
}

export function formatRemaining(timestamp, language = 'en') {
    if (!timestamp) return String(language).startsWith('zh') ? '永久' : 'Never'
    const seconds = Math.max(0, Number(timestamp) - Math.floor(Date.now() / 1000))
    if (seconds === 0) return String(language).startsWith('zh') ? '已到期' : 'Expired'
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    if (days > 0) return String(language).startsWith('zh') ? `${days}天 ${hours}小时` : `${days}d ${hours}h`
    const minutes = Math.max(1, Math.floor((seconds % 3600) / 60))
    return String(language).startsWith('zh') ? `${hours}小时 ${minutes}分` : `${hours}h ${minutes}m`
}
